import {  ExternalLink, Sparkles, Code, Palette, Zap, Server } from "lucide-react";
import { motion } from "framer-motion";

export const Portfolio = () => {
  const projects = [
    {
      name: "Revivers Shop",
      type: "E-commerce Clothing Website",
      category: "Clothing Brand",
      url: "http://reviversshop.com",
      icon: <Palette className="w-5 h-5" />
    },
    {
      name: "AF Group Tech",
      type: "Industrial Cleaning Website",
      category: "Cleaning Services",
      url: "https://afgroup.tech",
      icon: <Code className="w-5 h-5" />
    },
    // {
    //   name: "Rayane Trading",
    //   type: "E-commerce Perfume Brand",
    //   category: "Perfume Brand",
    //   url: "https://rayanetrading.com/",
    //   icon: <Zap className="w-5 h-5" />
    // },
    {
      name: "Dress Well Tailors",
      type: "E-commerce tailors Solution",
      category: "Tailors Solution",
      url: "https://dresswelltailors.com/",
      icon: <Zap className="w-5 h-5" />
    },
    {
      name: "Vital General Maintenance",
      type: "Maintenance & Repair Services",
      category: "Facility Management & Security",
      url: "http://vitalgeneralmaintenance.com/",
      icon: <Server className="w-5 h-5" />
    },
  ];

  return (
    <section
      id="portfolio"
      className="relative bg-gradient-to-br from-slate-50 via-blue-50 to-emerald-50 text-slate-900 py-24 px-6 scroll-mt-20 overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-emerald-400/20"
            style={{
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              width: `${Math.random() * 200 + 50}px`,
              height: `${Math.random() * 200 + 50}px`,
            }}
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: Math.random() * 15 + 15,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
        
       
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Heading */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: false }}
        >
          <motion.div
            className="inline-flex items-center justify-center mb-4"
            animate={{ rotate: [0, 10, -10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 5 }}
          >
            <Sparkles className="w-10 h-10 text-emerald-600 mr-3" />
            <h2 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 text-transparent bg-clip-text inline-block">
              Featured Projects
            </h2>
            <Sparkles className="w-10 h-10 text-blue-600 ml-3" />
          </motion.div>
          <p className="text-slate-700 mt-4 text-xl max-w-2xl mx-auto">
            A curated collection of my recent work, showcasing innovative solutions across various industries
          </p>
        </motion.div>

        {/* Projects Grid */}
        <div className="grid lg:grid-cols-2 gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: false }}
              className="group relative"
            >
              <motion.div
                whileHover={{ y: -10 }}
                transition={{ duration: 0.3 }}
                className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 h-full border border-slate-200 shadow-lg hover:shadow-xl overflow-hidden relative"
              >
                {/* Hover effect background */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-100/50 to-emerald-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                
                <div className="flex justify-between items-start mb-6 relative z-10">
                  <div className="flex items-center gap-4">
                    <motion.div 
                      whileHover={{ rotate: 15, scale: 1.1 }}
                      transition={{ type: "spring", stiffness: 300 }}
                      className="p-3 rounded-xl bg-gradient-to-br from-blue-500 to-emerald-500 text-white"
                    >
                      {project.icon}
                    </motion.div>
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors">
                        {project.name}
                      </h3>
                      <p className="text-slate-600 text-sm mt-1">{project.type}</p>
                    </div>
                  </div>
                  <motion.div
                    whileHover={{ scale: 1.2 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    onClick={() => window.open(project.url, "_blank")}
                    className="cursor-pointer"
                  >
                    <ExternalLink className="w-5 h-5 text-slate-500 group-hover:text-blue-600 transition-colors" />
                  </motion.div>
                </div>

                <div className="mb-6 relative z-10">
                  <span className="inline-block px-3 py-1 bg-gradient-to-r from-blue-100 to-emerald-100 text-blue-700 rounded-full text-xs font-medium border border-blue-200">
                    {project.category}
                  </span>
                </div>

                <motion.div
                  className="relative z-10"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  viewport={{ once: false }}
                >
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-sm font-medium bg-gradient-to-r from-blue-600 to-emerald-600 text-transparent bg-clip-text hover:from-blue-700 hover:to-emerald-700 transition-all group/view"
                  >
                    View project
                    <svg 
                      className="w-4 h-4 ml-2 group-hover/view:translate-x-1 transition-transform" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </motion.div>

                {/* Decorative elements */}
                <div className="absolute -bottom-4 -right-4 w-20 h-20 rounded-full bg-emerald-400/30 blur-xl"></div>
                <div className="absolute -top-4 -left-4 w-16 h-16 rounded-full bg-blue-400/30 blur-xl"></div>
              </motion.div>
            </motion.div>
          ))}
        </div>

        {/* Call to action */}
        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          viewport={{ once: false }}
        >
          <p className="text-slate-700 mb-6">Interested in seeing more of our completed projects or learning more about our services? feel free to contact us</p>
          <motion.a
            href="#contact"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-emerald-600 text-white font-medium rounded-2xl shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all duration-300"
          >
            Start a project
            <Sparkles className="w-5 h-5 ml-2" />
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};